import { isValidElement, memo, type ReactNode } from "react"

import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import { focusSourceRow, matchSourceIndex } from "@/lib/citations"
import { isSharePointUrl } from "@/lib/sharepoint"
import { usePreview } from "@/providers/PreviewProvider"
import type { Source } from "@/types/api"

function nodeText(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children)
  if (Array.isArray(children)) return children.map(nodeText).join("")
  if (isValidElement(children)) return nodeText((children.props as { children?: ReactNode }).children)
  return ""
}

interface MarkdownProps {
  content: string
  /** Sources as displayed (same order) in the message's SourceList; enables numbered citation badges. */
  sources?: Source[]
  /** Scopes the Sources row ids so the badge scrolls to the row of its own message. */
  messageId?: string
}

function CitationBadge({ n, source, messageId }: { n: number; source: Source; messageId: string }) {
  return (
    <button
      type="button"
      onClick={() => focusSourceRow(messageId, n)}
      aria-label={`Source ${n}: ${source.title}`}
      title={source.title}
      className="ml-0.5 inline-flex size-4 -translate-y-1 cursor-pointer items-center justify-center rounded-full bg-orange align-baseline text-[10px] leading-none font-bold text-white no-underline transition-colors hover:bg-orange-press focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
    >
      {n}
    </button>
  )
}

/** Markdown renderer — SharePoint links open the in-app document preview panel;
 * other http(s) links (wiki, Board help) open in a new tab since third-party
 * sites generally refuse to be framed (X-Frame-Options/CSP). */
export const Markdown = memo(function Markdown({ content, sources, messageId }: MarkdownProps) {
  const { openPreview } = usePreview()

  return (
    <div className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a({ href, children }) {
            const n = messageId ? matchSourceIndex(href, sources) : null
            const badge =
              n !== null && messageId && sources ? (
                <CitationBadge n={n} source={sources[n - 1]} messageId={messageId} />
              ) : null
            if (href && isSharePointUrl(href)) {
              return (
                <>
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault()
                      openPreview({
                        url: href,
                        title: nodeText(children) || "Document",
                      })
                    }}
                  >
                    {children}
                  </a>
                  {badge}
                </>
              )
            }
            return (
              <>
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
                {badge}
              </>
            )
          },
          // Never load model-supplied images: the browser would fetch the URL immediately
          // (and on every history reload), so a prompt-injected image URL carrying data is a
          // zero-click exfiltration channel. Show the alt text instead; the URL is not rendered.
          img({ alt }) {
            return alt ? <span className="text-muted-foreground">[{alt}]</span> : null
          },
          table({ children }) {
            return (
              <div className="overflow-x-auto">
                <table>{children}</table>
              </div>
            )
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
})
